import Button from "@/components/common/Button";
import Card from "@/components/common/Card";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface SecuritySettingsProps {
  user: any;
}

const SecuritySettings = ({ }: SecuritySettingsProps) => {
  const { t } = useTranslation();

  // Change Password state and logic
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState("");

  const hasMinLength = newPassword.length >= 12;
  const hasLettersNumbersSymbols =
    /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).*$/.test(
      newPassword
    );
  const isPasswordValid = hasMinLength && hasLettersNumbersSymbols;
  const doPasswordsMatch =
    newPassword === confirmPassword && confirmPassword.length > 0;
  const isCurrentPasswordEntered = currentPassword.length > 0;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPasswordValid || !doPasswordsMatch || !isCurrentPasswordEntered)
      return;
    setChangePasswordLoading(true);
    setChangePasswordError("");
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1200));
      setChangePasswordSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        setShowChangePassword(false);
        setChangePasswordSuccess(false);
      }, 2000);
    } catch {
      setChangePasswordError("Failed to change password. Please try again.");
    } finally {
      setChangePasswordLoading(false);
    }
  };

  if (changePasswordSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex min-h-[400px] items-center justify-center"
      >
        <Card className="p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
            <svg
              className="h-8 w-8 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2">
            {changePasswordSuccess}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t("settings.passwordChangedSuccessfully")}
          </p>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="mb-6 text-xl font-semibold text-gray-900 dark:text-white">
        {t("settings.security")}
      </h2>

      <div className="space-y-6">
        {/* Password Security */}
        <Card className="p-6">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
            {t("settings.passwordSecurity")}
          </h3>
          {!showChangePassword ? (
            <Button
              onClick={() => setShowChangePassword(true)}
              variant="outline"
            >
              {t("settings.changePassword")}
            </Button>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="text-sm font-medium text-slate-300">
                  Current Password
                </label>
                <div className="relative mt-1">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="text-sm font-medium text-slate-300">
                  New Password
                </label>
                <div className="relative mt-1">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {/* Password Requirements */}
                <div className="mt-2 space-y-1">
                  <div className={`text-xs ${hasMinLength ? "text-green-600" : "text-gray-500"}`}>
                    • At least 12 characters
                  </div>
                  <div className={`text-xs ${hasLettersNumbersSymbols ? "text-green-600" : "text-gray-500"}`}>
                    • Letters, numbers, and symbols
                  </div>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="text-sm font-medium text-slate-300">
                  Confirm Password
                </label>
                <div className="relative mt-1">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {confirmPassword.length > 0 && (
                  <div className={`mt-1 text-xs ${doPasswordsMatch ? "text-green-600" : "text-red-600"}`}>
                    {doPasswordsMatch ? "Passwords match" : "Passwords do not match"}
                  </div>
                )}
              </div>

              {changePasswordError && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                  {changePasswordError}
                </div>
              )}

              <div className="flex space-x-3">
                <Button
                  type="submit"
                  disabled={!isPasswordValid || !doPasswordsMatch || !isCurrentPasswordEntered || changePasswordLoading}
                  loading={changePasswordLoading}
                >
                  {t("settings.updatePassword")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowChangePassword(false);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setChangePasswordError("");
                  }}
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </form>
          )}
        </Card>

        {/* Two-Factor Authentication */}
        <Card className="p-6">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
            {t("settings.twoFactorAuth")}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t("settings.twoFactorAuthDescription")}
          </p>
          <Button variant="outline">
            {t("settings.enable2FA")}
          </Button>
        </Card>

        {/* Session Management */}
        <Card className="p-6">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
            {t("settings.sessionManagement")}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t("settings.sessionManagementDescription")}
          </p>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Current Session
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {navigator.userAgent.split(" ").slice(-1)[0].split("/")[0]} • Ethiopia
                </p>
              </div>
              <Button variant="outline" size="sm">
                {t("settings.terminate")}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </motion.div>
  );
};

export default SecuritySettings;

